import { OneOnOnesScheduledMeetingItemFragment } from 'generated/resources/graphql'
import moment from 'moment'

// Helper function to get the effective end time of a meeting
const getMeetingEndTime = (meeting: OneOnOnesScheduledMeetingItemFragment): moment.Moment => {
  return meeting.endTime ? moment(meeting.endTime) : moment(meeting.startTime).add(1, 'hour')
}

// Helper function to classify meetings as past or future
const classifyMeetings = (
  scheduledMeetings: OneOnOnesScheduledMeetingItemFragment[]
): {
  futureMeetings: OneOnOnesScheduledMeetingItemFragment[]
  pastMeetings: OneOnOnesScheduledMeetingItemFragment[]
} => {
  if (scheduledMeetings.length === 0) {
    return { futureMeetings: [], pastMeetings: [] }
  }

  const now = moment()

  // Filter meetings that have a start time
  const meetingsWithStartTime = scheduledMeetings.filter((meeting) => meeting.startTime)

  if (meetingsWithStartTime.length === 0) {
    return { futureMeetings: [], pastMeetings: [] }
  }

  // Separate future and past meetings
  const futureMeetings: OneOnOnesScheduledMeetingItemFragment[] = []
  const pastMeetings: OneOnOnesScheduledMeetingItemFragment[] = []

  meetingsWithStartTime.forEach((meeting) => {
    const endTime = getMeetingEndTime(meeting)

    if (endTime.isAfter(now)) {
      futureMeetings.push(meeting)
    } else {
      pastMeetings.push(meeting)
    }
  })

  // Sort future meetings by start time (earliest first)
  futureMeetings.sort((a, b) => moment(a.startTime).diff(moment(b.startTime)))

  // Sort past meetings by start time (most recent first)
  pastMeetings.sort((a, b) => moment(b.startTime).diff(moment(a.startTime)))

  return { futureMeetings, pastMeetings }
}

export const selectBestMeeting = (
  scheduledMeetings: OneOnOnesScheduledMeetingItemFragment[]
): OneOnOnesScheduledMeetingItemFragment | null => {
  const { futureMeetings, pastMeetings } = classifyMeetings(scheduledMeetings)

  if (futureMeetings.length > 0) {
    // Return the earliest future meeting
    return futureMeetings[0] ?? null
  }

  if (pastMeetings.length > 0) {
    // Return the most recent past meeting
    return pastMeetings[0] ?? null
  }

  return null
}

export const getDefaultMeetings = (
  scheduledMeetings: OneOnOnesScheduledMeetingItemFragment[]
): {
  upcomingMeetings: OneOnOnesScheduledMeetingItemFragment[]
  pastMeetings: OneOnOnesScheduledMeetingItemFragment[]
} => {
  const { futureMeetings, pastMeetings } = classifyMeetings(scheduledMeetings)

  return {
    upcomingMeetings: futureMeetings.slice(0, 1),
    pastMeetings: pastMeetings.slice(0, 4),
  }
}
